import { composeClassNames, withDefault } from "@faedev/editor-utils";
import { CSSProperties, useEffect, useState } from "react";
import { Badge } from "react-bootstrap";
import Text from "src/components/Text";
import Translate from "src/components/Translate";
import { sBadge } from "src/styles/sBadge.module.scss";
import { sPriceVariants } from "src/styles/sPriceVariants.module.scss";
import { VariantColor } from "src/types/VariantColor";
import { VariantSize } from "src/types/VariantSize";

type Price = number | { price: number; label?: string };

type Props = {
  className?: (string | string) | null | undefined;
  currency: string;
  price: Price;
  priceAlternatives: Price[];
  setNewPrice: (setNewPrice: Price) => void;
  size?: VariantSize;
  style?: CSSProperties | null | undefined;
  title?: string | null | undefined;
  variant?: VariantColor;
};

const PriceVariants = ({
  className,
  currency,
  price,
  priceAlternatives,
  setNewPrice,
  size,
  style,
  title,
  variant,
}: Props) => {
  const [activePrice, setActivePrice] = useState(price);

  const firstPriceEqual =
    priceAlternatives && priceAlternatives.length > 0
      ? (typeof priceAlternatives[0] === "number" &&
          priceAlternatives[0] !== price) ||
        (typeof priceAlternatives[0] !== "number" &&
          priceAlternatives[0].price !== price)
        ? false
        : true
      : false;

  useEffect(() => {
    firstPriceEqual && setActivePrice(priceAlternatives[0]);
  }, [firstPriceEqual, priceAlternatives]);

  const handleClick = (itemPrice: Price) => {
    setActivePrice(itemPrice);
    setNewPrice(itemPrice);
  };

  const Pill = ({ itemPrice }) => (
    <Badge
      pill={true}
      variant={withDefault(variant, "secondary")}
      className={composeClassNames([
        sBadge,
        "badge-light",
        "pill",
        itemPrice === activePrice ? "active" : "",
      ])}
      onClick={() => {
        handleClick(itemPrice);
      }}
    >
      <Text size={size} width={`semibold`} className="price-final">
        {typeof itemPrice === `number` ? (
          <>
            {itemPrice} <Translate tKey={currency} />
          </>
        ) : (
          <>
            <small>{itemPrice.label}</small>
            <br />
            {itemPrice.price} <Translate tKey={currency} />
          </>
        )}
      </Text>
    </Badge>
  );

  return (
    <div
      className={composeClassNames([
        sPriceVariants,
        "price-variants",
        className,
      ])}
      style={style}
    >
      <Text size={size} className="price-title">
        <Translate tKey={title ? title : `Price`} />:
      </Text>
      <div className="price-alternatives">
        {priceAlternatives && priceAlternatives.length > 0 ? (
          priceAlternatives.map((currentPrice, index) => {
            return (
              <>
                {index === 0 && !firstPriceEqual && <Pill itemPrice={price} />}
                <Pill itemPrice={currentPrice} key={`price-${currentPrice}`} />
              </>
            );
          })
        ) : (
          <Pill itemPrice={price} />
        )}
      </div>
    </div>
  );
};

export default PriceVariants;
